import React, { useState, useEffect } from "react";
import {
  Card,
  Switch,
  Typography,
  Button,
  message,
  Spin,
  InputNumber,
  Input
} from "antd";
import {
  MessageOutlined,
  ScanOutlined,
  StarOutlined,
  RocketOutlined,
  ArrowUpOutlined,
  BarChartOutlined
} from "@ant-design/icons";
import clientBillingApi from "../store/api";
import { ENTITLEMENT_IDS } from "configs/entitlements";
import "./UpgradesPage.scss";

const { Title, Text } = Typography;

// Internal Drivers entitlement ID
const INTERNAL_DRIVERS_ENTITLEMENT_ID = "5c2b11b0-7ed9-410c-b736-8b1fe3aba06a";

// SFTP POD Upload entitlement ID
const SFTP_POD_ENTITLEMENT_ID = "d38eafe1-6679-4d6b-8161-38b34f9bec2a";

// eslint-disable-next-line no-misleading-character-class -- intentionally matching individual zero-width/invisible Unicode characters; no ranges used
const ZERO_WIDTH_REGEX = /[\u200B\u200C\u200D\uFEFF\u034F\u2060\u2800\u3164\uFFA0]/;
const FQDN_OR_IP_REGEX =
  /^(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}|(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateSftpPodSettings = (settings) => {
  const errors = [];

  if (!settings.sftpHost) {
    errors.push("SFTP host is required");
  } else if (settings.sftpHost.length > 253) {
    errors.push("SFTP host must be 253 characters or less");
  } else if (!FQDN_OR_IP_REGEX.test(settings.sftpHost)) {
    errors.push("SFTP host must be a valid domain name or IPv4 address");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpHost)) {
    errors.push("SFTP host must not contain invisible characters");
  }

  const port = Number(settings.sftpPort);
  if (!settings.sftpPort) {
    errors.push("SFTP port is required");
  } else if (!Number.isInteger(port) || port < 1 || port > 65535) {
    errors.push("SFTP port must be a whole number between 1 and 65535");
  }

  if (!settings.sftpUser) {
    errors.push("SFTP username is required");
  } else if (settings.sftpUser.length > 64) {
    errors.push("SFTP username must be 64 characters or less");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpUser)) {
    errors.push("SFTP username must not contain invisible characters");
  }

  if (!settings.sftpPassword) {
    errors.push("SFTP password is required");
  } else if (settings.sftpPassword.length > 256) {
    errors.push("SFTP password must be 256 characters or less");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpPassword)) {
    errors.push("SFTP password must not contain invisible characters");
  }

  if (settings.sftpPath) {
    if (!settings.sftpPath.startsWith("/")) {
      errors.push("SFTP remote path must start with /");
    } else if (settings.sftpPath.length > 512) {
      errors.push("SFTP remote path must be 512 characters or less");
    } else if (ZERO_WIDTH_REGEX.test(settings.sftpPath)) {
      errors.push("SFTP remote path must not contain invisible characters");
    }
  }

  if (settings.notificationEmail) {
    if (!EMAIL_REGEX.test(settings.notificationEmail)) {
      errors.push("Notification email must be a valid email address");
    } else if (settings.notificationEmail.length > 254) {
      errors.push("Notification email must be 254 characters or less");
    }
  }

  return errors;
};

const UpgradesPage = () => {
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentMonthSmsCount, setCurrentMonthSmsCount] = useState(0);

  // State for Internal Drivers seat configuration
  const [internalDriversSeats, setInternalDriversSeats] = useState({});

  // State for SFTP POD settings
  const [sftpPodSettings, setSftpPodSettings] = useState({
    sftpHost: "",
    sftpPort: 22,
    sftpUser: "",
    sftpPassword: "",
    sftpPath: "/",
    notificationEmail: "",
    emailOnFailureOnly: true
  });

  useEffect(() => {
    fetchClientEntitlements();
  }, []);

  const fetchClientEntitlements = async () => {
    try {
      setLoading(true);
      const [entitlementsResponse, smsUsageResponse] = await Promise.all([
        clientBillingApi.getClientEntitlements(),
        clientBillingApi.getCurrentMonthSmsUsage()
      ]);
      setEntitlements(entitlementsResponse);
      setCurrentMonthSmsCount(smsUsageResponse.currentMonthCount);

      // Initialize seat configuration for Internal Drivers entitlement
      const internalDriversEnt = entitlementsResponse?.find(
        (e) => e.id === INTERNAL_DRIVERS_ENTITLEMENT_ID
      );
      if (internalDriversEnt) {
        setInternalDriversSeats({
          seats: internalDriversEnt.settings?.seats || 0,
          perSeatCost:
            internalDriversEnt.settings?.perSeatCost !== undefined
              ? internalDriversEnt.settings.perSeatCost
              : parseFloat(internalDriversEnt.cost) || 0
        });
      }

      // Initialize SFTP POD settings
      const sftpPodEnt = entitlementsResponse?.find(
        (e) => e.id === SFTP_POD_ENTITLEMENT_ID
      );
      if (sftpPodEnt?.settings) {
        setSftpPodSettings({
          sftpHost: sftpPodEnt.settings.sftpHost || "",
          sftpPort: sftpPodEnt.settings.sftpPort || 22,
          sftpUser: sftpPodEnt.settings.sftpUser || "",
          sftpPassword: sftpPodEnt.settings.sftpPassword || "",
          sftpPath: sftpPodEnt.settings.sftpPath || "/",
          notificationEmail: sftpPodEnt.settings.notificationEmail || "",
          emailOnFailureOnly:
            sftpPodEnt.settings.emailOnFailureOnly !== undefined
              ? sftpPodEnt.settings.emailOnFailureOnly
              : true
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (entitlementId, checked) => {
    setEntitlements((prev) =>
      prev.map((entitlement) =>
        entitlement.id === entitlementId
          ? { ...entitlement, client_is_active: checked }
          : entitlement
      )
    );
    setHasChanges(true);
  };

  const handleInternalDriversSeatsChange = (field, value) => {
    setInternalDriversSeats((prev) => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validate SFTP settings if that entitlement is active
    const sftpEntitlement = entitlements.find((e) => e.id === SFTP_POD_ENTITLEMENT_ID);
    if (sftpEntitlement?.client_is_active) {
      const errors = validateSftpPodSettings(sftpPodSettings);
      if (errors.length > 0) {
        errors.forEach((err) => message.error(err, 4));
        return;
      }
    }

    try {
      setSaving(true);

      let whiteLabelEntitlementChanged = false;
      let warehouseEntitlementChanged = false;
      const changedEntitlements = [];

      // Update all entitlements
      for (const entitlement of entitlements) {
        // Include entitlement-specific settings
        let settings = undefined;
        if (entitlement.id === INTERNAL_DRIVERS_ENTITLEMENT_ID) {
          settings = internalDriversSeats;
        } else if (
          entitlement.id === SFTP_POD_ENTITLEMENT_ID &&
          entitlement.client_is_active
        ) {
          // Only send settings when enabling; strip empty strings so @IsOptional() backend
          // validators are correctly skipped for unpopulated optional fields (e.g. notificationEmail).
          settings = Object.fromEntries(
            Object.entries(sftpPodSettings).filter(([, v]) => v !== "")
          );
        }

        await clientBillingApi.updateClientEntitlement(
          entitlement.id,
          entitlement.client_is_active || false,
          settings
        );

        // Track which entitlements were updated
        changedEntitlements.push(entitlement.id);

        // Check specific entitlements for special handling
        if (entitlement.id === ENTITLEMENT_IDS.WHITE_LABEL) {
          whiteLabelEntitlementChanged = true;
        }
        if (entitlement.id === ENTITLEMENT_IDS.WAREHOUSE) {
          warehouseEntitlementChanged = true;
        }
      }

      message.success("Entitlements updated successfully");
      setHasChanges(false);

      // Dispatch events for specific entitlements
      if (whiteLabelEntitlementChanged) {
        console.log(
          "Dispatching entitlementStatusChanged event for white label entitlement"
        );
        window.dispatchEvent(
          new CustomEvent("entitlementStatusChanged", {
            detail: { entitlementId: ENTITLEMENT_IDS.WHITE_LABEL }
          })
        );
      }

      if (warehouseEntitlementChanged) {
        console.log(
          "Dispatching entitlementStatusChanged event for warehouse entitlement"
        );
        window.dispatchEvent(
          new CustomEvent("entitlementStatusChanged", {
            detail: { entitlementId: ENTITLEMENT_IDS.WAREHOUSE }
          })
        );
      }

      // Dispatch general entitlement change event
      console.log("Dispatching general entitlementStatusChanged event");
      window.dispatchEvent(
        new CustomEvent("entitlementStatusChanged", {
          detail: { changedEntitlements }
        })
      );
    } catch (error) {
      console.error("Error updating entitlements:", error);
      message.error("Failed to update entitlements");
    } finally {
      setSaving(false);
    }
  };

  // Icon mapping for entitlements
  const getIconForEntitlement = (name) => {
    const iconMap = {
      "SMS Notification": (
        <MessageOutlined style={{ fontSize: "40px", color: "#1278F1" }} />
      ),
      "Document Scanning": (
        <ScanOutlined style={{ fontSize: "40px", color: "#6c757d" }} />
      ),
      "Priority Support": <StarOutlined style={{ fontSize: "40px", color: "#6c757d" }} />,
      "Advanced Analytics": (
        <BarChartOutlined style={{ fontSize: "40px", color: "#6c757d" }} />
      ),
      "Custom Branding": (
        <RocketOutlined style={{ fontSize: "40px", color: "#6c757d" }} />
      ),
      "API Access": <ArrowUpOutlined style={{ fontSize: "40px", color: "#6c757d" }} />
    };
    return (
      iconMap[name] || <MessageOutlined style={{ fontSize: "40px", color: "#6c757d" }} />
    );
  };

  // Static cards for the other 5 inactive features
  const staticCards = [
    {
      title: "Document Scanning",
      description: "AI-powered high resolution scanning to track paperwork.",
      price: "$0.25 / scan",
      enabled: false,
      isStatic: true
    },
    {
      title: "Priority Support",
      description: "Get dedicated support with faster response times.",
      price: "$19.99 / month",
      enabled: false,
      isStatic: true
    },
    {
      title: "Advanced Analytics",
      description: "Detailed insights and custom reporting for your operations.",
      price: "$14.99 / month",
      enabled: false,
      isStatic: true
    },
    {
      title: "Custom Branding",
      description: "White-label solution with your company branding.",
      price: "$29.99 / month",
      enabled: false,
      isStatic: true
    },
    {
      title: "API Access",
      description: "Full API access for custom integrations and automation.",
      price: "$24.99 / month",
      enabled: false,
      isStatic: true
    }
  ];

  // Helper function to format price with term
  const formatPriceWithTerm = (cost, term) => {
    if (!cost || cost === 0) return "Free";

    let termSuffix = "";
    if (term === 0) {
      termSuffix = " /ea";
    } else if (term === 30) {
      termSuffix = " /month";
    }

    const numericCost = parseFloat(cost);

    // If amount is less than $1 and has sub-penny precision, show as cents
    if (numericCost < 1) {
      const cents = numericCost * 100;
      // Remove trailing zeros for cents display
      const centsFormatted =
        cents % 1 === 0 ? cents.toFixed(0) : cents.toFixed(1).replace(/\.?0+$/, "");
      return `${centsFormatted}¢${termSuffix}`;
    }

    // For dollar amounts, check if there are cents
    if (numericCost % 1 === 0) {
      // No cents, show as whole dollars
      return `$${numericCost.toFixed(0)}${termSuffix}`;
    } else {
      // Has cents, show full amount
      return `$${numericCost.toFixed(2)}${termSuffix}`;
    }
  };

  // Combine real entitlements with static cards
  const upgradeCards = [
    ...entitlements.map((entitlement) => ({
      id: entitlement.id,
      title: entitlement.name,
      description: entitlement.description,
      price: formatPriceWithTerm(entitlement.cost, entitlement.term),
      enabled: entitlement.client_is_active || false,
      isStatic: false,
      icon: getIconForEntitlement(entitlement.name)
    })),
    ...staticCards.map((card, index) => ({
      id: `static-${index}`,
      ...card,
      icon: getIconForEntitlement(card.title)
    }))
  ];

  return (
    <div className="upgrades-page">
      <div className="upgrades-wrapper">
        <div className="upgrades-header">
          <div>
            <Title level={2} style={{ margin: 0, color: "#004dbc" }}>
              Upgrade Your Plan
            </Title>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              Enhance your delivery operations with powerful add-ons and premium features.
            </Text>
          </div>
          {hasChanges && (
            <Button
              type="primary"
              size="large"
              loading={saving}
              onClick={handleSave}
              style={{ marginLeft: "auto" }}
            >
              Save Changes
            </Button>
          )}
        </div>

        <Spin spinning={loading}>
          <div className="upgrades-card-container">
            {upgradeCards.map((card) => (
              <Card
                key={card.id}
                className={`upgrade-card ${card.isStatic ? "upgrade-card-disabled" : ""}`}
              >
                <div className="upgrade-card-content">
                  <div className="card-icon">{card.icon}</div>
                  <Title level={4} className="card-title">
                    {card.title}
                  </Title>
                  <Text className="card-description">{card.description}</Text>
                  {card.title === "SMS Notification" &&
                    card.id === "54f77fa7-d315-47a1-87ba-24de485137c4" && (
                      <div
                        className="card-usage"
                        style={{ margin: "8px 0", color: "#666", fontSize: "14px" }}
                      >
                        Current month total SMS: {currentMonthSmsCount}
                      </div>
                    )}

                  {/* Internal Drivers per-seat configuration */}
                  {card.id === INTERNAL_DRIVERS_ENTITLEMENT_ID && card.enabled && (
                    <div
                      style={{
                        margin: "12px 0",
                        padding: "12px",
                        background: "#f5f5f5",
                        borderRadius: "4px"
                      }}
                    >
                      <div style={{ marginBottom: "8px" }}>
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "4px"
                          }}
                        >
                          Number of Seats:
                        </Text>
                        <InputNumber
                          min={0}
                          value={internalDriversSeats?.seats || 0}
                          onChange={(value) =>
                            handleInternalDriversSeatsChange("seats", value)
                          }
                          style={{ width: "100%" }}
                          size="small"
                        />
                      </div>
                      <div style={{ marginBottom: "4px" }}>
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "4px"
                          }}
                        >
                          Cost per Seat: ${internalDriversSeats?.perSeatCost || 0}
                        </Text>
                      </div>
                      {internalDriversSeats?.seats > 0 &&
                        internalDriversSeats?.perSeatCost > 0 && (
                          <div>
                            <Text
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#1890ff"
                              }}
                            >
                              Total: $
                              {(
                                (internalDriversSeats?.seats || 0) *
                                (internalDriversSeats?.perSeatCost || 0)
                              ).toFixed(2)}
                              /month
                            </Text>
                          </div>
                        )}
                    </div>
                  )}

                  {/* SFTP POD settings panel */}
                  {card.id === SFTP_POD_ENTITLEMENT_ID && card.enabled && (
                    <div
                      style={{
                        margin: "12px 0",
                        padding: "12px",
                        background: "#f0f5ff",
                        borderRadius: "6px",
                        border: "1px solid #d6e4ff",
                        textAlign: "left"
                      }}
                    >
                      <Text
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "block",
                          marginBottom: "8px",
                          color: "#1d39c4"
                        }}
                      >
                        SFTP Connection
                      </Text>
                      <div style={{ marginBottom: "6px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Host:
                        </Text>
                        <Input
                          size="small"
                          placeholder="sftp.example.com"
                          value={sftpPodSettings.sftpHost}
                          onChange={(e) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              sftpHost: e.target.value
                            }));
                            setHasChanges(true);
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: "6px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Port:
                        </Text>
                        <InputNumber
                          size="small"
                          min={1}
                          max={65535}
                          value={sftpPodSettings.sftpPort}
                          onChange={(value) => {
                            setSftpPodSettings((prev) => ({ ...prev, sftpPort: value }));
                            setHasChanges(true);
                          }}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ marginBottom: "6px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Username:
                        </Text>
                        <Input
                          size="small"
                          placeholder="sftp_user"
                          value={sftpPodSettings.sftpUser}
                          onChange={(e) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              sftpUser: e.target.value
                            }));
                            setHasChanges(true);
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: "6px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Password:
                        </Text>
                        <Input.Password
                          size="small"
                          placeholder="••••••••"
                          value={sftpPodSettings.sftpPassword}
                          onChange={(e) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              sftpPassword: e.target.value
                            }));
                            setHasChanges(true);
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Remote Path:
                        </Text>
                        <Input
                          size="small"
                          placeholder="/uploads/pod/"
                          value={sftpPodSettings.sftpPath}
                          onChange={(e) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              sftpPath: e.target.value
                            }));
                            setHasChanges(true);
                          }}
                        />
                      </div>
                      <Text
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "block",
                          marginBottom: "8px",
                          color: "#1d39c4"
                        }}
                      >
                        Notifications
                      </Text>
                      <div style={{ marginBottom: "6px" }}>
                        <Text
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "block",
                            marginBottom: "3px"
                          }}
                        >
                          Notification Email:
                        </Text>
                        <Input
                          size="small"
                          placeholder="ops@example.com"
                          value={sftpPodSettings.notificationEmail}
                          onChange={(e) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              notificationEmail: e.target.value
                            }));
                            setHasChanges(true);
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "6px"
                        }}
                      >
                        <Switch
                          size="small"
                          checked={sftpPodSettings.emailOnFailureOnly !== false}
                          onChange={(checked) => {
                            setSftpPodSettings((prev) => ({
                              ...prev,
                              emailOnFailureOnly: checked
                            }));
                            setHasChanges(true);
                          }}
                        />
                        <Text style={{ fontSize: "11px" }}>
                          {sftpPodSettings.emailOnFailureOnly !== false
                            ? "Email on failure only"
                            : "Email on success and failure"}
                        </Text>
                      </div>
                    </div>
                  )}

                  <div className="card-price">{card.price}</div>
                  <div className="card-toggle">
                    <Switch
                      checked={card.enabled}
                      onChange={(checked) =>
                        !card.isStatic && handleToggle(card.id, checked)
                      }
                      size="default"
                      disabled={card.isStatic}
                    />
                    <Text
                      style={{
                        marginLeft: "8px",
                        color: card.enabled && !card.isStatic ? "#52c41a" : "#8c8c8c"
                      }}
                    >
                      {card.enabled
                        ? "Enabled"
                        : card.isStatic
                        ? "Coming Soon"
                        : "Disabled"}
                    </Text>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default UpgradesPage;
